/**
 * Advanced mode — `/design`.
 *
 * The floor planner + 3D designer offered as a fourth project type on step 1
 * (ModeStep). Opens in the planner; a blank blueprint shows the form to
 * create its room. The visitor keeps several blueprints (one room each —
 * unless the admin allows more) in localStorage and works on one at a time:
 * the 3D view shows the active one only.
 *
 * The designer itself is `DesignerHost` (shared with the sandbox page). This
 * page adds: the row's layout (Select · Fit · Reset · Space · Rooms · Undo ·
 * Redo · Get Estimate · Help · Exit), the blueprints and their store, Reset,
 * Help, and Exit back to the wizard.
 *
 * Get Estimate (once the room has product on it): a still of the room is
 * taken from the 3D view and its export held for the render job, the plan's
 * materials become the estimate (EstimatorContext.loadDesignerEstimate), and
 * the wizard's results page opens on them. All of it before leaving this
 * page — the 3D view is gone once the route changes.
 *
 * The host is mounted once. Switching blueprints, New and Reset open the
 * next document IN PLACE through `apiRef.load`, so the 3D view keeps its
 * WebGL context and only rebuilds the room — and the view the visitor is in
 * (planner or 3D) is kept across a switch.
 *
 * Reached only when Advanced mode is on (the route guard in App.tsx).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calculator, CircleQuestionMark, LogOut, RotateCcw } from 'lucide-react';
import { DesignerHost, DesignerHostApi, HostControls } from '../components/designer/DesignerHost';
import { DEFAULT_BRAND, DesignerBrand } from '../components/designer/chrome/DesignerHeader';
import { IconTextButton } from '../components/designer/chrome/IconTextButton';
import { DesignerModal } from '../components/designer/chrome/DesignerModal';
import { BlueprintsMenu } from '../components/designer/advanced/BlueprintsMenu';
import { DesignerHelpModal } from '../components/designer/advanced/DesignerHelpModal';
import { ResumePrompt } from '../components/designer/advanced/ResumePrompt';
import {
  AdvancedBlueprint, AdvancedState, OPEN_SAVED_DESIGN, asksToResume, blankBlueprint, blueprintName, freshAdvancedState, loadAdvancedState,
  saveAdvancedState,
} from '../components/designer/advanced/advancedStorage';
import { usePropLibrary } from '../components/designer/usePropLibrary';
import { designerDocument } from '../components/designer/designerDocument';
import { DESIGNER_STRINGS as S } from '../components/designer/strings';
import { EMPTY_PLAN, hasFinishedSection, renameRoom } from '../calculations/plan';
import { designFingerprint, holdRenderAsset } from '../components/designer/advanced/renderAsset';
import { useAppSettings } from '../context/AppSettingsContext';
import { usePartnerBranding } from '../context/PartnerBrandingContext';
import { useEstimator } from '../context/EstimatorContext';

type Modal = null | { kind: 'help' } | { kind: 'reset' } | { kind: 'delete'; id: string };

export const AdvancedDesigner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useAppSettings();
  const { partner } = usePartnerBranding();
  const { state: wizard, loadDesignerEstimate } = useEstimator();
  const { variants: propVariants, catalogue, finishes, looks } = usePropLibrary();
  const maxRooms = settings.advancedDesignerMultiRoom ? undefined : 1;

  // The blueprints: read once, written on every change.
  const [state, setState] = useState<AdvancedState>(() => loadAdvancedState({ maxRooms }) ?? freshAdvancedState());
  useEffect(() => { saveAdvancedState(state); }, [state]);
  // A design kept from last time is offered back, not just opened — unless the
  // way in says to open it (Back to Edit, a template, a reload once answered).
  const [askResume, setAskResume] = useState(() => asksToResume(location.state, state));
  const [modal, setModal] = useState<Modal>(null);
  const apiRef = useRef<DesignerHostApi | null>(null);
  // Get Estimate: taking the still and the export, then off to the results page.
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const active = state.blueprints.find((b) => b.id === state.activeId) ?? state.blueprints[0];
  const activeId = active.id;

  // The designer covers the page; the page behind it must not scroll.
  useEffect(() => {
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = before; };
  }, []);

  // A retailer's logo on the brand row, as on the estimator's own nav bar.
  const brand = useMemo<DesignerBrand>(() => ({
    logoSrc: partner?.logoUrl || DEFAULT_BRAND.logoSrc,
    logoAlt: partner?.logoAlt || partner?.name || DEFAULT_BRAND.logoAlt,
  }), [partner]);

  // Every change of the active document goes into its blueprint.
  const onChange = useCallback((doc: { plan: typeof EMPTY_PLAN; focusRoomId: string | null }, view: { plannerOpen: boolean; ui: AdvancedState['ui'] }) => {
    setState((s) => ({
      ...s,
      plannerOpen: view.plannerOpen,
      ui: view.ui,
      blueprints: s.blueprints.map((b) => (b.id === activeId ? { ...b, plan: doc.plan, focusRoomId: doc.focusRoomId, updatedAt: Date.now() } : b)),
    }));
  }, [activeId]);

  // ---------- Blueprints ----------
  // Open a blueprint in the mounted host. The view is kept (3D stays 3D) when
  // the blueprint has its room; without one the planner — and its create
  // form — must show, and stay showing once the room is made.
  const openInHost = useCallback((bp: AdvancedBlueprint, plannerOpen: boolean) => {
    const open = plannerOpen || bp.plan.rooms.length === 0;
    apiRef.current?.load(designerDocument(bp.plan, bp.focusRoomId), { plannerOpen: open });
    setState((s) => ({ ...s, activeId: bp.id, plannerOpen: open, ui: { lockedFace: null, selectedSectionId: null } }));
  }, []);
  const openBlueprint = useCallback((id: string) => {
    const bp = state.blueprints.find((b) => b.id === id);
    if (bp && bp.id !== activeId) openInHost(bp, state.plannerOpen);
  }, [state, activeId, openInHost]);
  const newBlueprint = useCallback(() => {
    const bp = blankBlueprint(`Room ${state.blueprints.length + 1}`);
    setState((s) => ({ ...s, blueprints: [...s.blueprints, bp] }));
    openInHost(bp, true);
  }, [state.blueprints.length, openInHost]);
  const renameBlueprint = useCallback((id: string, name: string) => {
    // The active one is the host's document: rename through it, so it is one undo step and the sheet follows.
    const api = apiRef.current;
    if (id === activeId && api && api.plan.rooms[0]) {
      const renamed = renameRoom(api.plan, api.plan.rooms[0].id, name);
      if (renamed.ok) api.commit(renamed.plan);
      return;
    }
    setState((s) => ({
      ...s,
      blueprints: s.blueprints.map((b) => {
        if (b.id !== id) return b;
        const room = b.plan.rooms[0];
        const renamed = room ? renameRoom(b.plan, room.id, name) : null;
        return { ...b, name, plan: renamed?.ok ? renamed.plan : b.plan, updatedAt: Date.now() };
      }),
    }));
  }, [activeId]);
  const deleteBlueprint = useCallback((id: string) => {
    const rest = state.blueprints.filter((b) => b.id !== id);
    if (rest.length === 0) {
      // The last one: a fresh blank takes its place.
      const fresh = freshAdvancedState();
      setState(fresh);
      openInHost(fresh.blueprints[0], true);
      return;
    }
    setState((s) => ({ ...s, blueprints: s.blueprints.filter((b) => b.id !== id) }));
    if (id === activeId) {
      // The one on screen: its neighbour takes over, in the same view.
      const at = state.blueprints.findIndex((b) => b.id === id);
      openInHost(rest[Math.min(at, rest.length - 1)], state.plannerOpen);
    }
  }, [state, activeId, openInHost]);
  const resetBlueprint = useCallback(() => {
    const blank: AdvancedBlueprint = { ...active, plan: EMPTY_PLAN, focusRoomId: null, updatedAt: Date.now() };
    setState((s) => ({ ...s, blueprints: s.blueprints.map((b) => (b.id === activeId ? blank : b)) }));
    openInHost(blank, true);
  }, [active, activeId, openInHost]);

  // ---------- Continue or start new ----------
  // Answered once: a reload of the page carries on without asking again.
  const answerResume = useCallback((startNew: boolean) => {
    setAskResume(false);
    navigate(location.pathname, { replace: true, state: OPEN_SAVED_DESIGN });
    if (!startNew) return;
    const fresh = freshAdvancedState();
    setState(fresh);
    openInHost(fresh.blueprints[0], true);
  }, [navigate, location.pathname, openInHost]);

  // ---------- Get Estimate ----------
  const canEstimate = hasFinishedSection(active.plan);
  const getEstimate = useCallback(async () => {
    if (!canEstimate || busy) return;
    setBusy(true);
    try {
      // The 3D view must be drawing: under the planner it is suspended, and a
      // fresh scene compiles for a moment, so the capture is asked for until it answers.
      if (state.plannerOpen) apiRef.current?.closePlanner();
      const deadline = Date.now() + 8000;
      let cap: Awaited<ReturnType<NonNullable<DesignerHostApi['capture']>>> = null;
      while (!cap && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 120));
        const capture = apiRef.current?.capture;
        cap = capture ? await capture({ width: 1920, height: 1080 }) : null;
      }
      const api = apiRef.current;
      if (!cap || !api) { setNotice(S.planner.estimateFailed); return; }
      const assetKey = `${activeId}-${Date.now().toString(36)}`;
      const doc = designerDocument(api.plan, api.focusRoomId);
      if (cap.glb && cap.manifest) holdRenderAsset({ key: assetKey, glb: cap.glb, manifest: cap.manifest, hash: designFingerprint(doc, cap.manifest) });
      await loadDesignerEstimate({
        doc,
        preview: { image: cap.image, camera: cap.camera, width: 1920, height: 1080, takenAt: new Date().toISOString(), assetKey },
      });
      navigate('/');
    } finally {
      setBusy(false);
    }
  }, [canEstimate, busy, state.plannerOpen, activeId, loadDesignerEstimate, navigate]);

  // ---------- The rows ----------
  const roomsMenu = (
    <BlueprintsMenu
      blueprints={state.blueprints.map((b) => ({ id: b.id, name: blueprintName(b), hasRoom: b.plan.rooms.length > 0 }))}
      activeId={activeId}
      onSwitch={openBlueprint}
      onNew={newBlueprint}
      onRename={renameBlueprint}
      onDelete={(id) => setModal({ kind: 'delete', id })}
    />
  );
  // Planner: Select · Fit · [Reset · Space · Rooms] · Undo · Redo · [Help · Exit].
  // 3D: the host's own controls as today, with Rooms among them.
  const headerSlot = (host: HostControls, view: { planner: boolean }) => (view.planner ? (
    <>
      <IconTextButton icon={<RotateCcw size={14} />} label={S.planner.reset} title={S.planner.resetTitle} onClick={() => setModal({ kind: 'reset' })} />
      {host.spaceMenu}
      {roomsMenu}
    </>
  ) : (
    <>
      {host.plannerButton}
      {host.roomShown}
      {host.spaceMenu}
      {roomsMenu}
      {host.stage}
      {host.clearStaging}
      {host.previewModels}
      {host.badge}
    </>
  ));
  const headerEnd = (
    <>
      <IconTextButton
        accent
        icon={<Calculator size={14} />}
        label={S.planner.getEstimate}
        title={canEstimate ? S.planner.getEstimateTitle : S.planner.getEstimateDisabled}
        aria-disabled={!canEstimate}
        className={canEstimate ? undefined : 'is-disabled'}
        onClick={canEstimate ? getEstimate : undefined}
      />
      <IconTextButton icon={<CircleQuestionMark size={15} />} label={S.planner.help} title={S.planner.helpTitle} onClick={() => setModal({ kind: 'help' })} />
      <IconTextButton icon={<LogOut size={14} />} label={S.planner.exit} title={S.planner.exitTitle} onClick={() => navigate('/')} />
    </>
  );

  const deleting = modal?.kind === 'delete' ? state.blueprints.find((b) => b.id === modal.id) : undefined;

  return (
    <>
      <DesignerHost
        initial={designerDocument(active.plan, active.focusRoomId)}
        initialPlannerOpen={state.plannerOpen}
        initialUi={state.ui}
        onChange={onChange}
        title={S.planner.title}
        title3d={S.designSpace}
        brand={brand}
        headerSlot={headerSlot}
        headerEnd={headerEnd}
        maxRooms={maxRooms}
        plannerEscapeCloses={false}
        plannerBackButton={false}
        initialSpaceType={wizard.applicationConfig.spaceType}
        apiRef={apiRef}
        stagingSeed={activeId}
        propVariants={propVariants}
        catalogue={catalogue}
        finishes={finishes}
        looks={looks}
        // No render controls bar for customers (2026-10-29): the space's look
        // lights the view and the render; the bar lives on in /sandbox/designer
        // and the Photoreal window in the Look Lab (docs/DESIGNER-V3.md).
        renderControls={false}
      />

      {askResume && <ResumePrompt state={state} onContinue={() => answerResume(false)} onStartNew={() => answerResume(true)} />}

      {busy && (
        <DesignerModal title={S.planner.preparingEstimate} dismissable={false}>
          <p>{S.planner.preparingEstimateBody}</p>
        </DesignerModal>
      )}
      {notice && (
        <DesignerModal title={S.planner.getEstimate} onClose={() => setNotice(null)}>
          <p>{notice}</p>
          <div className="dh-modal-actions">
            <button type="button" className="dh-btn is-accent" autoFocus onClick={() => setNotice(null)}>{S.rooms.cancel}</button>
          </div>
        </DesignerModal>
      )}

      {modal?.kind === 'help' && <DesignerHelpModal onClose={() => setModal(null)} drawTool={maxRooms === undefined} />}

      {modal?.kind === 'reset' && (
        <DesignerModal title={S.planner.reset} onClose={() => setModal(null)}>
          <p>{S.planner.resetConfirm(blueprintName(active))}</p>
          <div className="dh-modal-actions">
            <button type="button" className="dh-btn" onClick={() => setModal(null)}>{S.rooms.cancel}</button>
            <button type="button" className="dh-btn is-accent" autoFocus onClick={() => { setModal(null); resetBlueprint(); }}>{S.planner.resetNow}</button>
          </div>
        </DesignerModal>
      )}

      {modal?.kind === 'delete' && deleting && (
        <DesignerModal title={S.rooms.delete} onClose={() => setModal(null)}>
          <p>{S.rooms.deleteConfirm(blueprintName(deleting))}</p>
          <div className="dh-modal-actions">
            <button type="button" className="dh-btn" onClick={() => setModal(null)}>{S.rooms.cancel}</button>
            <button type="button" className="dh-btn is-accent" autoFocus onClick={() => { setModal(null); deleteBlueprint(deleting.id); }}>{S.rooms.deleteNow}</button>
          </div>
        </DesignerModal>
      )}
    </>
  );
};

export default AdvancedDesigner;
